import React, { useContext, useEffect, useState } from 'react';
import shortid from 'shortid';
import { isEmpty, isUndefined } from 'lodash';
import { injectIntl, useIntl } from 'react-intl';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { withRouter } from 'react-router-dom';
import {
  getTranslatedText,
  AppContext,
  toast,
  getDefaultValue,
  getDateOperators,
  ALL_TABLES,
  saveTableRowCount,
  getAllColumnWidth,
  getColumnWidth as superTableColumnWidth,
  saveColumnWidth,
  getPersonalizedSorting,
  useActivityTracker,
  getCurrentUserHook,
  SuperList,
  useSuperListHook,
  COMMON_CONSTANTS
} from '@nile/components';
import { useUserClientCheck } from '@nile/components/src/Utils/helpers';
import {
  AccountClient,
  GET_INTEGRATIONS_BY_IDS,
  RMMClient,
  GET_PERSONALIZATION_BY_NAME,
  GET_ENTITY_NAMES_META
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  GET_ASSET_META_INFO_V4,
  GET_WORK_STATION_META_INFO_V4,
  RESTORE_ASSET,
  HARD_DELETE_ASSET,
  SOFT_DELETE_ASSETS_V2,
  RUN_LIVE_ACTION_FOR_ASSETS_V2,
  UPDATE_ASSET_CLIENT_AND_SITE_V2
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import RMMContext from '@nile/rmm/src/modules/common/contexts/RMMContext';
import { TRASH_ASSET } from '@nile/rmm/src/modules/common/constants';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import {
  columnsConfig,
  getAttributeName,
  getCategory,
  getColumnWidth,
  getColumnAccessor,
  getColumnCellType,
  getFieldsToBeRequested,
  getNetworkFieldsToBeRequested
} from '@nile/rmm/src/modules/asset-management/services/asset-list-helper';
import {
  getVariables,
  checkDynamicQuery,
  getDynamicQuery,
  generateOptions,
  fetchValues,
  fetchValuesGroupValues,
  getAsyncOptions,
  getGroupAsyncOptions,
  setConfig,
  getConfig
} from '@nile/rmm/src/modules/asset-management/services/asset-filter-helper';
import {
  defaultOptions,
  assetDetailsTab,
  INTEGRATIONS,
  INTEGRATIONS_NON_SOLO
} from '@nile/rmm/src/modules/asset-management/constants/asset-list';
import withPlanFeatureGating from '@nile/components/src/HOC/withPlanFeatureGating';
import { getEntityVariables } from '@nile/rmm/src/modules/asset-management/components/sub-pages/all-monitored-assets-view/all-monitored-assets-view-helper';
import { getAssetsEntityType } from '@nile/rmm/src/constants';
import { buildSelectionInput } from '@nile/rmm/src/modules/common/utils/pathUtils';
import { useParams } from 'react-router-dom';

function AssetManagementViewList(props) {
  const {
    flags,
    source,
    classId,
    activeView,
    onlyDefaultColumns,
    defaultColumnsToExclude,
    defaultFilters,
    isClientDetailsPage = false,
    padded = false,
    checkBoxSize,
    isWorkStationAsset,
    noBulkSelect = false,
    columnsOnly,
    actionColumn,
    noAssetActions = false,
    defaultShowFilter = false,
    scriptView = false,
    tab
  } = props;

  const [assetList, setAssetList] = useState([]);
  const [configuredCols, setConfiguredCols] = useState(null);
  const [tableBodyLoader, setTableBodyLoader] = useState(false);
  const [columnGroup, setColumnGroup] = useState(null);
  const intl = useIntl();
  const viewId = tab;

  const appContext = useContext(AppContext);
  const tabContext = useContext(TicketContext);
  const rmmContext = useContext(RMMContext);
  const { chargeBeePlanId } = appContext?.user;
  const user = getCurrentUserHook();
  const tableName = ALL_TABLES.asset;
  const isAssetInClientPortal = useUserClientCheck();
  const isEnabledBulkSelection = isAssetInClientPortal || noBulkSelect;
  const columnWidthObject = getAllColumnWidth(tableName);
  const personalizedSorting = getPersonalizedSorting(tableName, viewId);

  const isAssetView = source === 'ASSET_VIEW';

  const {
    updateTableFilterCondition,
    tableCondition,
    advanceFilterCondition,
    records,
    setRecords
  } = useSuperListHook({
    tableName,
    viewId: viewId
  });

  const fetchEntityMeta = async () => {
    const response = await RMMClient.query({
      query: GET_ENTITY_NAMES_META,
      variables: {
        entitiesName:
          isAssetView && isUndefined(activeView?.moduleType)
            ? [
                'WorkStation',
                'Client',
                'Site',
                'Requester',
                'policyCategory',
                'BusinessFunction',
                'AssetCPUMemData'
              ]
            : getEntityVariables(activeView?.moduleType)
      }
    });
    return response?.data;
  };

  const getClientDetailsViewName = () => {
    return isAssetInClientPortal
      ? 'PortalClientDetailsAssetView'
      : 'ClientDetailsAssetView';
  };
  const fetchPersonalization = async () => {
    const response = await AccountClient.query({
      query: GET_PERSONALIZATION_BY_NAME,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: {
        userId: user.userId,
        category: isClientDetailsPage
          ? getClientDetailsViewName()
          : getCategory(source, isAssetInClientPortal),
        name: viewId
      }
    });
    const { getPersonalizationByCategoryAndName } = response?.data;
    return getPersonalizationByCategoryAndName?.value
      ? JSON.parse(getPersonalizationByCategoryAndName?.value)
      : [];
  };
  const fetchWorkStationMetaInfo = async () => {
    const response = await RMMClient.query({
      query: GET_WORK_STATION_META_INFO_V4,
      variables: {}
    });

    const {
      selectAttributes,
      searchAttributes,
      sortAttributes,
      defaultSelectedAttributes
    } = response.data.getWorkStationMetaInfoV4;
    const columnAttributes = isAssetInClientPortal
      ? defaultSelectedAttributes
      : selectAttributes;

    return [
      columnAttributes,
      searchAttributes,
      sortAttributes,
      defaultSelectedAttributes
    ];
  };
  const fetchAssetGroupMetaInfo = async () => {
    if (!isEmpty(classId)) {
      const response = await RMMClient.query({
        query: GET_ASSET_META_INFO_V4,
        variables: {
          classId
        }
      });
      const {
        selectAttributes,
        searchAttributes,
        sortAttributes,
        defaultSelectedAttributes
      } = response.data.getAssetMetaInfoV4;

      return [
        selectAttributes,
        searchAttributes,
        sortAttributes,
        defaultSelectedAttributes
      ];
    }
  };
  const getAssetGroupVariables = (params) => {
    const listInfo = params?.listInfo;
    const variables = {
      groupId: {
        value: params.groupId,
        type: 'ID',
        required: true
      },
      listInfo: {
        value: listInfo,
        type: 'ListInfoInput',
        required: true
      }
    };
    return variables;
  };

  const getAssetClassVariables = (params) => {
    const listInfo = params?.listInfo;
    const variables = {
      listInfo: {
        value: listInfo,
        type: 'ListInfoInput',
        required: true
      }
    };
    return variables;
  };

  /**
   * Resets bulk action state to initial values
   * Clears selected records, action type, and loader state
   */
  const resetBulkState = () => {
    setRecords([]);
    rmmContext?.setBulkActionType('');
    rmmContext?.setBulkRecords([]);
    rmmContext?.setBulkActionLoader(false);
  };

  /**
   * Performs bulk actions on selected assets
   * Handles various bulk operations like client changes, deletions, installations, and reboots
   * @param action - The type of bulk action to perform
   * @param selectedRecords - Array of selected records (legacy parameter, now uses context)
   */
  const performBulkAction = ({
    action,
    selectedRecords = []
  }: {
    action: string;
    selectedRecords?: any[];
  }) => {
    const { bulkRecords: contextBulkRecords, bulkActionData } = rmmContext;
    const bulkRecords = records?.totalRecords ? records : contextBulkRecords;
    switch (action) {
      case 'refreshListData':
        generateColumns();
        break;
      case 'CHANGE_CLIENT':
        {
          const selectionInput = buildSelectionInput({
            bulkRecords,
            viewId: viewId,
            viewSubType: activeView?.viewSubType
          });

          const input = {
            clientId: bulkActionData.clientId,
            siteId: bulkActionData.siteId,
            selectionInput
          };

          RMMClient.mutate({
            mutation: UPDATE_ASSET_CLIENT_AND_SITE_V2,
            variables: {
              input
            }
          })
            .then((res) => {
              if (res && res.data) {
                if (res?.data?.updateAssetsClientAndSiteV2) {
                  generateColumns();
                  toast(
                    'success',
                    'rmm.assetManagement.assetUpdate.successMsg'
                  );
                } else {
                  toast(
                    'error',
                    'rmm.assetManagement.bulk.updateAsset.errorMessage'
                  );
                }
              }
            })
            .catch(() => {
              toast(
                'error',
                'rmm.assetManagement.bulk.updateAsset.errorMessage'
              );
            })
            .finally(resetBulkState);
        }
        break;
      case 'SOFT_DELETE_ASSETS':
        {
          const selectionInput = buildSelectionInput({
            bulkRecords,
            viewId: viewId,
            viewSubType: activeView?.viewSubType
          });

          RMMClient.mutate({
            mutation: SOFT_DELETE_ASSETS_V2,
            variables: {
              input: { selectionInput }
            }
          })
            .then((res) => {
              if (res && res.data) {
                if (res?.data?.softDeleteAssetsV2) {
                  generateColumns();
                  toast(
                    'success',
                    'rmm.assetManagement.bulk.deleteAsset.successMsg'
                  );
                } else {
                  toast(
                    'error',
                    'rmm.assetManagement.bulk.deleteAsset.errorMessage'
                  );
                }
              }
            })
            .catch(() => {
              toast(
                'error',
                'rmm.assetManagement.bulk.deleteAsset.errorMessage'
              );
            })
            .finally(() => {
              setRecords([]);
              rmmContext.setBulkActionType('');
              rmmContext.setBulkRecords([]);
            });
        }
        break;
      case 'TRASH_RESTORE':
        RMMClient.mutate({
          mutation: RESTORE_ASSET,
          variables: {
            assetIds: selectedRecords.map((item) => item.assetId)
          }
        })
          .then((res) => {
            if (res && res.data) {
              if (res?.data?.restoreAssetsV2?.status === 'SUCCESS') {
                generateColumns();
                toast('success', 'rmm.assetManagement.assetRestore.successMsg');
                setRecords([]);
              } else if (
                res?.data?.restoreAssetsV2?.status === 'PARTIALLY_SUCCESS'
              ) {
                generateColumns();
                toast(
                  'warning',
                  'rmm.asset.restore.partial.success.message',
                  'en',
                  {
                    successCount: res?.data?.restoreAssetsV2?.successCount,
                    failedCount: res?.data?.restoreAssetsV2?.failureCount
                  }
                );
                setRecords([]);
              } else {
                toast(
                  'error',
                  'rmm.assetManagement.bulk.restoreAsset.errorMessage'
                );
                setRecords([]);
              }
            }
          })
          .catch((err) => {});
        break;
      case 'INSTALL_SPLASHTOP':
      case 'INSTALL_ISL':
        {
          const selectionInput = buildSelectionInput({
            bulkRecords,
            viewId: viewId,
            viewSubType: activeView?.viewSubType
          });

          RMMClient.mutate({
            mutation: RUN_LIVE_ACTION_FOR_ASSETS_V2,
            variables: {
              input: {
                windowInput: {
                  windowId: shortid.generate()
                },
                actionType: action,
                selectionInput
              }
            }
          })
            .then((res) => {
              const isSplashtop = action === 'INSTALL_SPLASHTOP';
              if (res && res.data) {
                if (res?.data?.runLiveActionsForAssetsV2) {
                  toast(
                    'success',
                    isSplashtop
                      ? 'asset.install.splashtop.success.message'
                      : 'asset.install.isl.success.message'
                  );
                } else {
                  toast(
                    'error',
                    isSplashtop
                      ? 'asset.install.splashtop.fail.message'
                      : 'asset.install.isl.fail.message'
                  );
                }
              } else {
                toast(
                  'error',
                  isSplashtop
                    ? 'asset.install.splashtop.fail.message'
                    : 'asset.install.isl.fail.message'
                );
              }
            })
            .catch(() => {
              toast('error', 'app.toast.asset.detail.reboot.fail');
            })
            .finally(resetBulkState);
        }
        break;
      case 'BULK_REBOOT_ASSETS':
        {
          const selectionInput = buildSelectionInput({
            bulkRecords,
            viewId: viewId,
            viewSubType: activeView?.viewSubType
          });

          RMMClient.mutate({
            mutation: RUN_LIVE_ACTION_FOR_ASSETS_V2,
            variables: {
              input: {
                windowInput: {
                  windowId: shortid.generate()
                },
                actionType: 'REBOOT',
                selectionInput
              }
            }
          })
            .then((res) => {
              if (res && res.data) {
                if (res?.data?.runLiveActionsForAssetsV2) {
                  toast('success', 'rmm.bulk.reboot.success.message', 'en', {
                    count: records?.totalRecords
                  });
                } else {
                  toast('error', 'app.toast.asset.detail.reboot.fail');
                }
              } else {
                toast('error', 'app.toast.asset.detail.reboot.fail');
              }
              useActivityTracker({
                event: `Asset - Reboot bulk`
              });
            })
            .catch(() => {
              toast('error', 'app.toast.asset.detail.reboot.fail');
            })
            .finally(resetBulkState);
        }
        break;
      case 'TRASH_DELETE_FOREVER':
        RMMClient.mutate({
          mutation: HARD_DELETE_ASSET,
          variables: {
            assetIds: selectedRecords.map((item) => item.assetId)
          }
        })
          .then((res) => {
            if (res && res.data) {
              if (res?.data?.hardDeleteAssetsV2?.status === 'SUCCESS') {
                setTableBodyLoader(true);
                generateColumns();
                toast('success', 'rmm.assetManagement.deleteAsset.successMsg');
                setRecords([]);
              } else if (
                res?.data?.hardDeleteAssetsV2?.status === 'PARTIALLY_SUCCESS'
              ) {
                setTableBodyLoader(true);
                generateColumns();
                toast(
                  'warning',
                  'rmm.assetManagement.deleteAsset.partial.successMsg',
                  'en',
                  {
                    successCount: res?.data?.hardDeleteAssetsV2?.successCount,
                    failedCount: res?.data?.hardDeleteAssetsV2?.failureCount
                  }
                );
                setRecords([]);
              } else {
                toast(
                  'error',
                  'rmm.assetManagement.bulk.deleteAsset.errorMessage'
                );
                setRecords([]);
              }
            }
          })
          .catch((err) => {});
        break;
      default:
        break;
    }
  };

  const performActions = (col) => {
    switch (col.name) {
      case 'name':
        return {
          onClick: navToDetailsPage
        };
      default:
        break;
    }
  };

  const actionMap = (selectedCell = {}) => {
    performBulkAction({
      action: selectedCell.meta.actionType,
      selectedRecords: [selectedCell]
    });
  };

  const isSortable = (col, sortAttributes) => {
    if (sortAttributes.includes(col.name)) {
      return true;
    }
    return false;
  };

  const isFilterable = (col, searchAttributes) => {
    if (searchAttributes.includes(col.name)) {
      return true;
    }
    return false;
  };
  const constructTableData = (
    cols,
    sortAttributes,
    searchAttributes,
    configuredIntegrations
  ) => {
    const workFromHomeEnabled =
      user?.remoteDesktopAddOnEnabledStatus?.SPLASHTOP_WORK_FROM_HOME ||
      user?.remoteDesktopAddOnEnabledStatus?.ISL_ONLINE_WORK_FROM_HOME;
    const columns = [];
    const showOnClientPortal = isAssetInClientPortal
      ? workFromHomeEnabled || user.splashtopWFHEnabled // on client portal, check for splashtop enabled
      : true; // display it by default on techportal
    if (showOnClientPortal && !noAssetActions) {
      columns.push({
        header: ' ',
        accessorKey: 'id',
        size: isAssetInClientPortal ? 150 : 50,
        Header: ' ',
        accessor: 'value',
        width: isAssetInClientPortal ? 150 : 40,
        type: 'ACTIONS',
        disableSortBy: true,
        configuredIntegrations,
        disableFilters: true,
        disableResizing: true,
        isAssetInClientPortal: isAssetInClientPortal
      });
    }

    // Remove below check once mac policy set is supported
    if (flags.macAsset) {
      columns.push({
        header: ' ',
        accessorKey: 'id',
        size: isAssetInClientPortal ? 100 : 50,
        Header: ' ',
        accessor: 'id',
        width: isAssetInClientPortal ? 100 : 40,
        type: 'TYPE',
        disableSortBy: true,
        disableFilters: true,
        disableResizing: true
      });
    }
    cols.forEach((col) => {
      columns.push({
        header: col.label,
        accessorKey: getColumnAccessor(col),
        minSize: getColumnWidth(col.name, true),
        size: superTableColumnWidth(columnWidthObject, col.name),
        Header: col.label,
        minWidth: getColumnWidth(col.name, true),
        width: getColumnWidth(col.name),
        accessor: getColumnAccessor(col),
        type: getColumnCellType(
          col,
          activeView,
          isAssetInClientPortal,
          scriptView
        ),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: !isSortable(col, sortAttributes),
        editable: false,
        disableFilters: !isFilterable(col, searchAttributes),
        field: col,
        actions: performActions(col),
        module: activeView?.moduleType
          ? getAssetsEntityType(activeView?.moduleType)
          : 'WorkStation',
        helpers: {
          getDefaultValue,
          checkDynamicQuery,
          updateConditions: updateTableFilterCondition,
          getDateOperators,
          getVariables: getVariables.bind(this, col),
          getDynamicQuery,
          generateOptions,
          fetchValues:
            source === 'ASSET_VIEW' ? fetchValues : fetchValuesGroupValues,
          getAsyncOptions:
            source === 'ASSET_VIEW' ? getAsyncOptions : getGroupAsyncOptions
        }
      });
    });

    if (actionColumn) {
      columns.push({
        header: '',
        accessorKey: actionColumn?.buttonName,
        size: 150,
        Header: '',
        accessor: actionColumn?.buttonName,
        pinned: 'left',
        type: 'BUTTON_ACTION',
        disableFilters: true,
        disableSortBy: true,
        width: 150,
        action: (selectedCell) => {
          actionColumn?.actionHandler(selectedCell);
        },
        meta: {
          icon: actionColumn?.iconName,
          buttonName: actionColumn?.buttonName,
          staticIcon: true,
          size: actionColumn?.size
        }
      });
    }

    //Action button added only for Trash asset list currently
    if (
      activeView &&
      activeView.systemViewProperty &&
      activeView.systemViewProperty === TRASH_ASSET
    ) {
      columns.push({
        header: '',
        accessorKey: 'buttonActionRestore',
        size: 150,
        Header: '',
        accessor: 'buttonActionRestore',
        pinned: 'right',
        type: 'BUTTON_ACTION',
        disableFilters: true,
        disableSortBy: true,
        width: 150,
        action: actionMap,
        meta: {
          icon: 'Restore',
          svgIcon: 'Restore',
          color: 'red',
          actionType: 'TRASH_RESTORE',
          buttonName: 'project.view.bulk.restore.button'
        }
      });
      columns.push({
        header: '',
        accessorKey: 'buttonActionDelete',
        Header: '',
        accessor: 'buttonActionDelete',
        pinned: 'right',
        type: 'BUTTON_ACTION',
        disableFilters: true,
        disableSortBy: true,
        action: actionMap,
        meta: {
          icon: 'DeleteRed',
          svgIcon: 'delete',
          color: 'red',
          actionType: 'TRASH_DELETE_FOREVER',
          buttonName: 'project.view.bulk.delete.button'
        }
      });
    }
    return columns;
  };

  const getOperation = () => {
    return source === 'ASSET_VIEW'
      ? 'getWorkStationList'
      : 'getAssetGroupAssetsV2';
  };

  const getGeneratorFields = () => {
    const workStationAsset =
      ['1', '2', '3'].indexOf(classId) !== -1 ||
      activeView?.viewSubType === 'WORKSTATION_ASSET' ||
      isWorkStationAsset ||
      isAssetInClientPortal;
    return workStationAsset
      ? getFieldsToBeRequested(configuredCols, isAssetInClientPortal)
      : getNetworkFieldsToBeRequested(configuredCols, isAssetInClientPortal);
  };
  const getWorkStationListVariables = () => {
    const config = getConfig();

    const isAssetView = source === 'ASSET_VIEW';
    const key = isAssetView ? 'viewId' : 'groupId';

    setConfig({
      ...config,
      [key]: viewId
    });

    return isAssetView ? {} : { groupId: viewId };
  };

  const navToDetailsPage = (obj) => {
    const { assetId, name, assetClass } = obj?.row?.original;
    tabContext.openTab({
      module: assetDetailsTab,
      data: {
        id: assetId,
        subject: name,
        classId: assetClass?.classId
      }
    });
  };
  const fetchIntegrationsByIds = async (variables) => {
    const response = await AccountClient.query({
      query: GET_INTEGRATIONS_BY_IDS,
      variables: variables.variables
    });
    const res = response?.data;
    if (res?.getIntegrationsByIds) {
      const activatedIntegrations = res?.getIntegrationsByIds.filter(
        (integration) => {
          return integration.activated === true;
        }
      );
      return activatedIntegrations;
    }
  };

  useEffect(() => {
    const { bulkRecords: contextBulkRecords, bulkActionType } = rmmContext;

    if (!bulkActionType || !contextBulkRecords || !records) return;
    if (bulkActionType === 'refreshListData') {
      performBulkAction({
        action: bulkActionType
      });
      return;
    }
    const bulkRecords =
      records?.totalRecords > 0 ? records : contextBulkRecords;
    let selectedRecords = [];
    let unSelectedRecords = [];
    if (Array.isArray(bulkRecords)) {
      selectedRecords = bulkRecords;
    } else {
      selectedRecords = bulkRecords?.selectedRecords || [];
      unSelectedRecords = bulkRecords?.unSelectedRecords || [];
    }

    const hasRecords =
      bulkRecords.allSelected ||
      unSelectedRecords.length > 0 ||
      selectedRecords.length > 0;

    if (hasRecords) {
      performBulkAction({
        action: bulkActionType,
        selectedRecords
      });
    }
  }, [rmmContext?.bulkActionType]);

  const generateColumns = async () => {
    setTableBodyLoader(true);
    const entityMeta = await fetchEntityMeta();
    const [
      allColumns,
      searchAttributes,
      sortAttributes,
      defaultSelectedAttributes
    ] =
      source === 'ASSET_VIEW'
        ? await fetchWorkStationMetaInfo()
        : await fetchAssetGroupMetaInfo();
    const columnPersonalization = await fetchPersonalization();
    let integrationIds =
      chargeBeePlanId === 'solo' ? INTEGRATIONS : INTEGRATIONS_NON_SOLO;
    const configuredIntegrations = await fetchIntegrationsByIds({
      variables: { integrationIds }
    });
    if (
      allColumns.length > 0 &&
      !isEmpty(entityMeta?.getEntitiesMeta) &&
      columnPersonalization !== null
    ) {
      const entities = entityMeta?.getEntitiesMeta;
      const { configuredCols, allCols } = getProcessedConfig({
        entitiesMeta: entities,
        allColumns,
        intl,
        module: activeView?.moduleType
          ? getAssetsEntityType(activeView?.moduleType)
          : 'WorkStation',
        columnPersonalization,
        searchAttributes,
        onlyDefaultColumns,
        defaultColumns: defaultSelectedAttributes,
        defaultColumnsToExclude,
        columnsOnly
      });
      setConfiguredCols(configuredCols);
      if (rmmContext !== 'rmm') {
        rmmContext.setColumnConfigurations({
          allColumns: [
            {
              name: 'columns',
              header: 'Columns',
              items: allCols
            }
          ],
          configuredColumns: configuredCols
        });
      }
      const columnGroup = constructTableData(
        configuredCols,
        sortAttributes,
        searchAttributes,
        configuredIntegrations
      );
      setColumnGroup(columnGroup);
      setTableBodyLoader(false);
    }
  };

  useEffect(() => {
    generateColumns();
  }, [viewId, source, classId, rmmContext?.updatedConfiguredColumns]);

  return (
    <div className="w100 fh x-hidden">
      <SuperList
        noBulkSelect={isEnabledBulkSelection}
        skeletonLoading={tableBodyLoader}
        columns={columnGroup}
        defaultColumnCells={columnsConfig}
        data={assetList}
        setData={setAssetList}
        getAttributeName={getAttributeName}
        skipPageReset={false}
        fullHeight
        emptyState={{
          nodataImage: `${envConfig.imageRoot}svg/asset-emplty-stat.svg`,
          heading: getTranslatedText(
            intl,
            activeView?.systemViewProperty === 'TRASH_ASSET'
              ? 'asset.trash.noAssetsFound.heading'
              : 'asset.noAssetsFound.heading'
          ),
          description: getTranslatedText(
            intl,
            activeView?.systemViewProperty === 'TRASH_ASSET'
              ? 'asset.trash.noAssetsFound.desc'
              : 'asset.noAssetsFound.desc'
          )
        }}
        ldFlagEnabled={flags?.assetSuperTable}
        user={user}
        saveTableRowCount={saveTableRowCount}
        saveColumnWidth={saveColumnWidth}
        tableName={tableName}
        columnWidthPersonalization
        padded={padded}
        checkBoxSize={checkBoxSize}
        sortingPersonalization
        viewId={viewId}
        appendViewIdToListInfo={source === 'ASSET_VIEW'}
        defaultShowFilter={defaultShowFilter}
        client={RMMClient}
        queryGenerator={{
          operation: getOperation(),
          getVariables:
            source === 'ASSET_VIEW'
              ? getAssetClassVariables
              : getAssetGroupVariables,
          getFields: getGeneratorFields
        }}
        accessorKey={
          source === 'ASSET_VIEW'
            ? 'getWorkStationList'
            : 'getAssetGroupAssetsV2'
        }
        dataKey="assets"
        tableCondition={tableCondition}
        advanceFilterCondition={advanceFilterCondition}
        records={records}
        setRecords={setRecords}
        defaultSort={personalizedSorting || defaultOptions.sort}
        defaultCondition={defaultFilters}
        additionalVariables={getWorkStationListVariables()}
        enableSelectAll
        maintainSelection
        idKey="assetId"
      />
    </div>
  );
}

export const WithNMFeatureGating = withPlanFeatureGating(
  withRouter(injectIntl(withLDConsumer()(AssetManagementViewList))),
  {
    feature: 'network-monitoring-system',
    id: 'AssetManagementViewList',
    mode: 'tease'
  }
);

export default withRouter(
  injectIntl(withLDConsumer()(AssetManagementViewList))
);
